import { useEffect, useRef, useState } from 'react'
import {
  Type, Sparkles, Stars, Play, Volume2, Smartphone, Bell, CheckCircle,
  ShieldCheck, RotateCcw, Trash2, ChevronRight, Info
} from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase/config'
import { toast } from '../hooks/useToast'
import fcmService from '../services/fcmService'
import { DEFAULT_PREFS, loadPrefs, savePrefs, vibrate, VERSION_LABEL } from '../utils/appPrefs'
import './SettingsPage.css'

/**
 * PARAMÈTRES — chaque réglage agit vraiment et s'applique tout de suite.
 * Enregistrement automatique (plus de bouton "Sauvegarder").
 * Connecté : synchronisé dans le cloud (collection userSettings).
 */

const VAPID_KEY = 'BJaNYtB0eZ-N8_iBXNpb1-7ZqBtjSBFBFHqniTbYFvxrKiioPQjIejqNhxR4srmiU2xQLKFcjO9u7amyQ5VycPE'
const isNative = Capacitor.isNativePlatform()

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`s-switch ${checked ? 'is-on' : ''}`}
      onClick={() => { vibrate(8); onChange(!checked) }}
    >
      <span />
    </button>
  )
}

function Row({ icon: Icon, title, desc, children, onClick }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag className={`s-row ${onClick ? 's-row--link' : ''}`} onClick={onClick} type={onClick ? 'button' : undefined}>
      <span className="s-row__icon"><Icon size={22} /></span>
      <span className="s-row__text">
        <span className="s-row__title">{title}</span>
        {desc && <span className="s-row__desc">{desc}</span>}
      </span>
      {children}
      {onClick && !children && <ChevronRight size={20} className="s-row__chev" />}
    </Tag>
  )
}

async function readNotifPermission() {
  try {
    if (isNative) {
      const { PushNotifications } = await import('@capacitor/push-notifications')
      const st = await PushNotifications.checkPermissions()
      return st.receive === 'granted' ? 'granted' : st.receive === 'denied' ? 'denied' : 'default'
    }
    return typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  } catch {
    return 'unsupported'
  }
}

export default function SettingsPage({ currentUser, globalAudio }) {
  const [prefs, setPrefs] = useState(() => loadPrefs())
  const [notif, setNotif] = useState('default')
  const [notifBusy, setNotifBusy] = useState(false)
  const cloudTimer = useRef(null)
  const loadedCloud = useRef(false)

  // Récupère les réglages du cloud à la connexion
  useEffect(() => {
    if (!currentUser || loadedCloud.current) return
    loadedCloud.current = true
    getDoc(doc(db, 'userSettings', currentUser.uid))
      .then((snap) => {
        const cloud = snap.exists() ? snap.data()?.prefs : null
        if (cloud) setPrefs(savePrefs({ ...loadPrefs(), ...cloud }))
      })
      .catch(() => { /* hors ligne : on garde le local */ })
  }, [currentUser])

  useEffect(() => { readNotifPermission().then(setNotif) }, [])

  useEffect(() => () => clearTimeout(cloudTimer.current), [])

  const change = (patch) => {
    const next = savePrefs({ ...prefs, ...patch })
    setPrefs(next)

    if (currentUser) {
      clearTimeout(cloudTimer.current)
      cloudTimer.current = setTimeout(() => {
        setDoc(doc(db, 'userSettings', currentUser.uid), {
          prefs: next, userId: currentUser.uid, updatedAt: serverTimestamp()
        }, { merge: true }).catch(() => {})
      }, 800)
    }
  }

  const changeVolume = (v) => {
    change({ defaultVolume: v })
    globalAudio?.setVolume?.(v)
    if (globalAudio?.audioRef?.current && !globalAudio.isMuted) {
      globalAudio.audioRef.current.volume = v / 100
    }
  }

  const enableNotifications = async () => {
    setNotifBusy(true)
    try {
      const token = await fcmService?.requestPermissionAndGetToken(VAPID_KEY)
      const perm = await readNotifPermission()
      setNotif(perm)
      if (token || perm === 'granted') toast.success('🔔 Notifications activées')
      else if (perm === 'denied') toast.error('Notifications bloquées : autorisez-les dans les réglages du téléphone')
      else toast.error("Impossible d'activer les notifications pour le moment")
    } catch {
      toast.error("Impossible d'activer les notifications pour le moment")
    } finally {
      setNotifBusy(false)
    }
  }

  const openAdChoices = async () => {
    try {
      const { AdMob } = await import('@capacitor-community/admob')
      await AdMob.showPrivacyOptionsForm()
    } catch {
      toast.info('Ce choix est disponible dans l\'application Android')
    }
  }

  const resetPrefs = () => {
    if (!window.confirm('Remettre tous les réglages par défaut ?')) return
    change({ ...DEFAULT_PREFS })
    toast.success('Réglages remis par défaut')
  }

  const clearLocal = () => {
    if (!window.confirm('Effacer les données enregistrées sur cet appareil (réveils, minuteur, préférences) ? Votre compte n\'est pas touché.')) return
    try {
      ['johnnyToolsAlarms', 'johnnyToolsTimer', 'johnnyToolsChrono', 'appSettings', 'sessionStartTime']
        .forEach((k) => localStorage.removeItem(k))
    } catch { /* rien */ }
    setPrefs(savePrefs({ ...DEFAULT_PREFS }))
    toast.success('Données locales effacées')
  }

  const TEXT_SIZES = [
    { id: 'normal', label: 'Normal', sample: 'Aa' },
    { id: 'large', label: 'Grand', sample: 'Aa' },
    { id: 'xlarge', label: 'Très grand', sample: 'Aa' }
  ]

  return (
    <div className="page active settings-page">
      <div className="s-wrap">
        <header className="s-head">
          <h1>Paramètres</h1>
          <p>Enregistrés automatiquement{currentUser ? ' et synchronisés avec votre compte' : ' sur cet appareil'}.</p>
        </header>

        {/* AFFICHAGE */}
        <section className="s-card">
          <h2 className="s-card__title">Affichage</h2>

          <div className="s-block">
            <div className="s-block__label"><Type size={20} /> Taille du texte</div>
            <div className="s-seg" role="radiogroup" aria-label="Taille du texte">
              {TEXT_SIZES.map((t, i) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={prefs.textSize === t.id}
                  className={`s-seg__opt ${prefs.textSize === t.id ? 'is-on' : ''}`}
                  onClick={() => { vibrate(8); change({ textSize: t.id }) }}
                >
                  <span style={{ fontSize: `${1.1 + i * 0.3}rem`, fontWeight: 800 }}>{t.sample}</span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <Row icon={Sparkles} title="Réduire les animations" desc="Moins de mouvements à l'écran, plus reposant">
            <Switch checked={prefs.reduceMotion} onChange={(v) => change({ reduceMotion: v })} label="Réduire les animations" />
          </Row>
          <Row icon={Stars} title="Fond étoilé animé" desc="Le désactiver économise la batterie">
            <Switch checked={prefs.backgroundFx} onChange={(v) => change({ backgroundFx: v })} label="Fond étoilé animé" />
          </Row>
        </section>

        {/* RADIO */}
        <section className="s-card">
          <h2 className="s-card__title">Radio</h2>

          <Row icon={Play} title="Lecture automatique" desc="La radio démarre dès l'ouverture de l'application">
            <Switch checked={prefs.autoplay} onChange={(v) => change({ autoplay: v })} label="Lecture automatique" />
          </Row>

          <div className="s-block">
            <div className="s-block__label">
              <Volume2 size={20} /> Volume au démarrage
              <strong className="s-block__value">{prefs.defaultVolume}%</strong>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={prefs.defaultVolume}
              onChange={(e) => changeVolume(parseInt(e.target.value, 10))}
              className="s-range"
              style={{ '--val': `${prefs.defaultVolume}%` }}
              aria-label="Volume au démarrage"
            />
          </div>

          <Row icon={Smartphone} title="Vibrations" desc="Petite vibration quand on appuie sur les boutons">
            <Switch checked={prefs.haptics} onChange={(v) => change({ haptics: v })} label="Vibrations" />
          </Row>
        </section>

        {/* NOTIFICATIONS */}
        <section className="s-card">
          <h2 className="s-card__title">Notifications</h2>
          {notif === 'granted' ? (
            <Row icon={CheckCircle} title="Notifications activées" desc="Vous recevez les annonces de 100% Johnny." />
          ) : (
            <>
              <Row
                icon={Bell}
                title={notif === 'denied' ? 'Notifications bloquées' : 'Recevoir les annonces'}
                desc={notif === 'denied'
                  ? 'Autorisez-les dans les réglages du téléphone, rubrique Applications.'
                  : 'Émissions spéciales, événements, anniversaires.'}
              />
              {notif !== 'denied' && notif !== 'unsupported' && (
                <button type="button" className="s-btn s-btn--gold" onClick={enableNotifications} disabled={notifBusy}>
                  {notifBusy ? 'Activation…' : 'Activer les notifications'}
                </button>
              )}
            </>
          )}
        </section>

        {/* CONFIDENTIALITÉ */}
        <section className="s-card">
          <h2 className="s-card__title">Confidentialité</h2>
          <Row
            icon={ShieldCheck}
            title="Mes choix publicitaires"
            desc="Modifier votre consentement pour les publicités"
            onClick={openAdChoices}
          />
        </section>

        {/* DONNÉES */}
        <section className="s-card">
          <h2 className="s-card__title">Données</h2>
          <Row icon={RotateCcw} title="Remettre les réglages par défaut" onClick={resetPrefs} />
          <Row icon={Trash2} title="Effacer les données de cet appareil" desc="Réveils, minuteur et préférences" onClick={clearLocal} />
        </section>

        <div className="s-foot">
          <Info size={16} /> {VERSION_LABEL}
        </div>
      </div>
    </div>
  )
}
